/* eslint-disable max-len */
// In this file always start exported variable names with "NMO_SEMATTRS_"

const ngNavNamespace = 'nmo.ng.navigation.';

/**
 * A unique ID that the router assigns to every router navigation, starting at 1 and increasing by 1 per navigation. (Angular Router)
 *
 * Note: Same as id property from RouterEvent https://angular.io/api/router/RouterEvent#id
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_ID = ngNavNamespace + 'id';

/**
 * Identifies the call or event that triggered the navigation. An imperative trigger is a call to router.navigateByUrl() or router.navigate().
 *
 * Note: Same as navigationTrigger property of NavigationStart event https://angular.io/api/router/NavigationStart#navigationTrigger
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_TRIGGER = ngNavNamespace + 'trigger';

/**
 * The URL that is the destination for this navigation. (Angular Router)
 *
 * Note: Same as url property from RouterEvent https://angular.io/api/router/RouterEvent#url
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_URL = ngNavNamespace + 'url';

/**
 * Path + query + fragment (all normalized) at NavigationStart or NavigationSkipped event (Angular Router)
 *
 * Note: Same as location.path(true) from https://angular.io/api/common/Location#path
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_REL_URL_START = ngNavNamespace + 'rel_url_start';

/**
 * Path + query + fragment (all normalized) at end event (NavigationEnd or NavigationCancel or NavigationError) or NavigationSkipped event (Angular Router)
 *
 * Note: Same as location.path(true) from https://angular.io/api/common/Location#path
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_REL_URL_END = ngNavNamespace + 'rel_url_end';

/**
 * Not documented by Angular, but this is after Router internal redirects (think "redirectTo": "some other route").
 *
 * Note: Same as urlAfterRedirects property from RoutesRecognized event https://angular.io/api/router/RoutesRecognized
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_URL_AFTER_REDIRECTS = ngNavNamespace + 'url_after_redirects';

/**
 * NavigationEnd | NavigationCancel | NavigationError. Last Angular RouterEvent for this navigation.
 *
 * Note: https://angular.io/guide/router-reference#router-events
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_END_EVENT = ngNavNamespace + 'end_event';

/**
 * A code to indicate why the navigation was canceled. This cancellation code is stable for the reason and can be relied on whereas the reason string could change and should not be used in production. (Angular Router)
 *
 * Note: Same as code property from NavigationCancel event https://angular.io/api/router/NavigationCancel#code
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_CANCELLATION_CODE = ngNavNamespace + 'cancellation_code';

/**
 * A description of why the navigation was cancelled. For debug purposes only. Use code instead for a stable cancellation reason that can be used in production. (Angular Router)
 *
 * Note: Same as reason property from NavigationCancel event https://angular.io/api/router/NavigationCancel#reason
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_CANCELLATION_REASON = ngNavNamespace + 'cancellation_reason';

/**
 * A code to indicate why the navigation was skipped. This code is stable for the reason and can be relied on whereas the reason string could change and should not be used in production. (Angular Router)
 *
 * Note: Same as code property from NavigationSkipped event https://angular.io/api/router/NavigationSkipped#code
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_SKIPPED_CODE = ngNavNamespace + 'skipped_code';

/**
 * A description of why the navigation was skipped. For debug purposes only. Use code instead for a stable skipped reason that can be used in production. (Angular Router)
 *
 * Note: Same as reason property from NavigationSkipped event https://angular.io/api/router/NavigationSkipped#reason
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_SKIPPED_REASON = ngNavNamespace + 'skipped_reason';

/**
 * Target URL for Router external redirect
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_REDIRECT_TARGET = ngNavNamespace + 'redirect_target';

/**
 * When true, navigates while replacing the current state in history. (Only applies if redirect is within same app)
 *
 * Note: See replaceURL https://angular.dev/api/router/NavigationBehaviorOptions#
 */
export const NMO_SEMATTRS_NMO_NG_NAVIGATION_REDIRECT_REPLACE_URL = ngNavNamespace + 'redirect_replace_url';

const idTokenClaimPrefix = 'nmo.oauth2.id_token_claim_';

/**
 * OAuth discovery document status either fetching, loaded or error
 */
export const NMO_SEMATTRS_NMO_OAUTH2_DISCOVERY_DOCUMENT_STATUS = 'nmo.oauth2.discovery_document.status';

/**
 * ID Token claim: Audience https://www.rfc-editor.org/rfc/rfc7519.html#section-4.1.3
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_AUD = idTokenClaimPrefix + 'aud';

/**
 * ID Token claim: Time when the authentication occurred https://www.iana.org/assignments/jwt/jwt.xhtml
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_AUTH_TIME = idTokenClaimPrefix + 'auth_time';

/**
 * ID Token claim: Preferred e-mail address	https://www.iana.org/assignments/jwt/jwt.xhtml
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_EMAIL = idTokenClaimPrefix + 'email';

/**
 * ID Token claim: Expiration Time https://www.rfc-editor.org/rfc/rfc7519.html#section-4.1.4
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_EXP = idTokenClaimPrefix + 'exp';

/**
 * ID Token claim: Issued At https://www.rfc-editor.org/rfc/rfc7519.html#section-4.1.6
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_IAT = idTokenClaimPrefix + 'iat';

/**
 * ID Token claim: Issuer https://www.rfc-editor.org/rfc/rfc7519.html#section-4.1.1
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_ISS = idTokenClaimPrefix + 'iss';

/**
 * ID Token claim: JWT ID https://www.rfc-editor.org/rfc/rfc7519.html#section-4.1.7
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_JTI = idTokenClaimPrefix + 'jti';

/**
 * ID Token claim: Session ID https://www.iana.org/assignments/jwt/jwt.xhtml
 */
export const NMO_SEMATTRS_NMO_OAUTH2_ID_TOKEN_CLAIM_SID = idTokenClaimPrefix + 'sid';

/**
 * OAuth login status of user either true or false
 */
export const NMO_SEMATTRS_NMO_OAUTH2_LOGIN_STATUS = 'nmo.oauth2.login.status';

/**
 * OAuth2 Mode. session-api or direct (to Hydra). See https://mohomedia.atlassian.net/wiki/spaces/SSO/pages/3420422145/OAuth2+Longer+Sessions
 */
export const NMO_SEMATTRS_NMO_OAUTH2_MODE = 'nmo.oauth2.mode';

/**
 * The session API decides what to do or return based on the type of session the user has, when doing token refresh aka session check. This can be one of: NO_SESSION -> No refresh token provided, RESILIENT_SESSION -> Already migrated to a more "resilient" session (Session API solution), MIGRATE_TO_RESILIENT_SESSION -> Coming from old solution but being migrated to more "resilient" session with this request.
 *
 * [Resilient or longer Sessions](https://mohomedia.atlassian.net/wiki/spaces/SSO/pages/3420422145/OAuth2+Longer+Sessions)
 */
export const NMO_SEMATTRS_NMO_OAUTH2_SESSION_API_SESSION_TYPE = 'nmo.oauth2.session_api.session_type';

/**
 * Based on the Hydra error response, it is clear that the session is lost and retrying will not be successful. [Docs](https://mohomedia.atlassian.net/wiki/spaces/SSO/pages/3308650497/Token+refresh+errors)
 */
export const NMO_SEMATTRS_NMO_OAUTH2_SESSION_API_SESSION_LOST = 'nmo.oauth2.session_api.session_lost';

/**
 * Different clients have different requirements for the interface (sending/receiving data). To accomodate this, we have an adapter which translates between the client compatible interfaces and one internal interface/representation.
 *
 * At the moment, we have 'browser' for browser clients (using Cookies and Headers) and 'hacky-spec' (using just body properties from OAuth2 spec but misusing one to pass on additional data, because News app only supports it this way)
 */
export const NMO_SEMATTRS_NMO_OAUTH2_SESSION_API_ADAPTER = 'nmo.oauth2.session_api.adapter';

/**
 * The saveId for a stored token from/in storage.
 *
 * Note: saveId is needed to later make sure that we only delete a record from storage, if the saved token is acknowledged to be received (by sending the saveId) and abort deletion if another token got saved for the same key - with a new saveId - in the meantime. See SessionStorageService for implementation details.
 */
export const NMO_SEMATTRS_NMO_OAUTH2_SESSION_API_STORAGE_SAVE_ID = 'nmo.oauth2.session_api.storage.save_id';

/**
 * String value of (incomplete/redacted) response body. Also see {@link NMO_SEMATTRS_NMO_HTTP_RESPONSE_BODY_PROPERTIES_PREFIX}
 *
 * Note: The body might be incomplete, redacted or not saved at all. Since it includes potentially sensitive data, the body has to be tracked on a per case basis (with some exceptions like error responses, atm).
 *
 * Also: For some responses assumptions during string conversion might have been made. So, don't expect this to be 100% accurate, especially if no value is set or if the value doesn't seem to make any sense. Check other attributes (like 'nmo.http.xhr_response_type' for xhr requests) for hints. Extend implementation as necessary.
 */
export const NMO_SEMATTRS_NMO_HTTP_RESPONSE_BODY_VALUE = 'nmo.http.response.body.value';

/**
 * Properties (incomplete/redacted) of a map/object like request body (e.g. JSON object, form data). Might (have to) be encoded as JSON, even if property in response is not JSON.
 *
 * Note: Properties might be incomplete, redacted or missing. Since the body can include sensitive data, these attributes have to mostly be added on a per case basis. It's a good idea to do this manually, when you're working on a route or need to improve visibility into certain inputs.
 *
 * Also: This is a bit experimental. If this isn't working well for you, please bring it up.
 */
export const NMO_SEMATTRS_NMO_HTTP_REQUEST_BODY_PROPERTIES_PREFIX = 'nmo.http.request.body.properties.';

/**
 * Properties (incomplete/redacted) of a map/object like response body (e.g. JSON object, form data). Might (have to) be encoded as JSON, even if property in response is not JSON.
 *
 * Also see {@link NMO_SEMATTRS_NMO_HTTP_RESPONSE_BODY_VALUE}, especially for (non-redacted) error responses.
 *
 * Note: Properties might be incomplete, redacted or missing. Since the body can include sensitive data, these attributes have to mostly be added on a per case basis. It's a good idea to do this manually, when you're working on a route or need to improve visibility into certain outputs.
 *
 * Note: This is a bit experimental. If this isn't working well for you, please bring it up.
 */
export const NMO_SEMATTRS_NMO_HTTP_RESPONSE_BODY_PROPERTIES_PREFIX = 'nmo.http.response.body.properties.';

/**
 * Request cookies (incomplete/redacted).
 *
 * Note: Values might be incomplete, redacted or missing. Since cookies can include sensitive data, these attributes have to mostly be added on a per case basis. It's a good idea to do this manually, when you're working on a route or need to improve visibility into certain inputs.
 *
 * Note: This is a bit experimental. If this isn't working well for you, please bring it up.
 */
export const NMO_SEMATTRS_NMO_HTTP_REQUEST_COOKIES_PREFIX = 'nmo.http.request.cookies.';

/**
 * Response cookies (incomplete/redacted). Use like 'nmo.http.response.set_cookie.<cookie-name>.value' and 'nmo.http.response.set_cookie.<cookie-name>.options_string' (options string like in set-cookie header)
 *
 * Note: Values might be incomplete, redacted or missing. Since cookies can include sensitive data, these attributes have to mostly be added on a per case basis. It's a good idea to do this manually, when you're working on a route or need to improve visibility into certain outputs.
 *
 * Note: This is a bit experimental. If this isn't working well for you, please bring it up.
 */
export const NMO_SEMATTRS_NMO_HTTP_RESPONSE_SET_COOKIE_PREFIX = 'nmo.http.response.set_cookie.';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState
 */
export const NMO_SEMATTRS_NMO_HTTP_XHR_READY_STATE = 'nmo.http.xhr_ready_state';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType
 */
export const NMO_SEMATTRS_NMO_HTTP_XHR_RESPONSE_TYPE = 'nmo.http.xhr_response_type';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Response/type
 */
export const NMO_SEMATTRS_NMO_HTTP_FETCH_RESPONSE_TYPE = 'nmo.http.fetch_response_type';

/**
 * On server: Message of "error", that explains or lead to 4xx response.
 *
 * Why do we need these extra "nmo.http.client_error." attributes? Because 4xx status codes indicate an error only for the client, not for the server. Therefore, we should NOT set the Span Status to error and NOT use exception attributes. [HTTP Span Status](https://opentelemetry.io/docs/specs/semconv/http/http-spans/#status)
 */
export const NMO_SEMATTRS_NMO_HTTP_CLIENT_ERROR_MESSAGE = 'nmo.http.client_error.message';

/**
 * On server: Type of "error", that explains or lead to 4xx response. Low cardinality information about what kind of error happened. Hint: Think about what you might like to group (GROUPBY) or filter (WHERE) in Queries used for debugging, visualizing, monitoring, alarming, ...
 *
 * Why do we need these extra "nmo.http.client_error." attributes? Because 4xx status codes indicate an error only for the client, not for the server. Therefore, we should NOT set the Span Status to error and NOT use exception attributes. [HTTP Span Status](https://opentelemetry.io/docs/specs/semconv/http/http-spans/#status)
 */
export const NMO_SEMATTRS_NMO_HTTP_CLIENT_ERROR_TYPE = 'nmo.http.client_error.type';

/**
 * On server: Stacktrace of "error", that explains or lead to 4xx response.
 *
 * Why do we need these extra "nmo.http.client_error." attributes? Because 4xx status codes indicate an error only for the client, not for the server. Therefore, we should NOT set the Span Status to error and NOT use exception attributes. [HTTP Span Status](https://opentelemetry.io/docs/specs/semconv/http/http-spans/#status)
 */
export const NMO_SEMATTRS_NMO_HTTP_CLIENT_ERROR_STACKTRACE = 'nmo.http.client_error.stacktrace';

/**
 * Event type of event from our OAuth2 library.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_TYPE = 'nmo.oauth.event.type';

/**
 * Error reason from error event from our OAuth2 library. This isn't well documented by the lib and "error reason" doesn't look like the best name. I JSON.stringify the object I'm getting from the lib. Soo, just see what you can do with the values.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_ERROR_REASON = 'nmo.oauth.event.error_reason';

/**
 * Value of Query Param "error" (used when communicating errors in OAuth2 process). Again, not really documented by the lib. So, I can't make a lot of guarantees whether this will always be present where applicable.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_ERROR_PARAM = 'nmo.oauth.event.error_param';

/**
 * Value of Query Param "error_description" (used when communicating errors in OAuth2 process). Again, not really documented by the lib. So, I can't make a lot of guarantees whether this will always be present where applicable.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_ERROR_DESCRIPTION_PARAM = 'nmo.oauth.event.error_description_param';

/**
 * Value of Query Param "error_hint" (used when communicating errors in OAuth2 process). Again, not really documented by the lib. So, I can't make a lot of guarantees whether this will always be present where applicable.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_ERROR_HINT_PARAM = 'nmo.oauth.event.error_hint_param';

/**
 * Value of Query Param "error_debug" (used when communicating errors in OAuth2 process). Again, not really documented by the lib. So, I can't make a lot of guarantees whether this will always be present where applicable.
 *
 * Note: https://github.com/manfredsteyer/angular-oauth2-oidc/blob/master/projects/lib/src/events.ts
 */
export const NMO_SEMATTRS_NMO_OAUTH_EVENT_ERROR_DEBUG_PARAM = 'nmo.oauth.event.error_debug_param';

/**
 * Name of the request handler function (NestJS).
 */
export const NMO_SEMATTRS_NMO_NESTJS_CALLBACK = 'nmo.nestjs.callback';

/**
 * Name of the class/controller where the request handler is specified in (NestJS).
 */
export const NMO_SEMATTRS_NMO_NESTJS_CONTROLLER = 'nmo.nestjs.controller';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState
 */
export const NMO_SEMATTRS_NMO_BROWSER_DOCUMENT_READY_STATE = 'nmo.browser.document.ready_state';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Document/timeline
 */
export const NMO_SEMATTRS_NMO_BROWSER_DOCUMENT_TIMELINE_CURRENT_TIME = 'nmo.browser.document.timeline.current_time';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/cookieEnabled
 */
export const NMO_SEMATTRS_NMO_BROWSER_COOKIE_ENABLED = 'nmo.browser.cookie_enabled';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine
 */
export const NMO_SEMATTRS_NMO_BROWSER_ON_LINE = 'nmo.browser.on_line';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export const NMO_SEMATTRS_NMO_BROWSER_CONNECTION_DOWNLINK = 'nmo.browser.connection.downlink';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export const NMO_SEMATTRS_NMO_BROWSER_CONNECTION_EFFECTIVE_TYPE = 'nmo.browser.connection.effective_type';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export const NMO_SEMATTRS_NMO_BROWSER_CONNECTION_RTT = 'nmo.browser.connection.rtt';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export const NMO_SEMATTRS_NMO_BROWSER_CONNECTION_SAVE_DATA = 'nmo.browser.connection.save_data';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
export const NMO_SEMATTRS_NMO_BROWSER_CONNECTION_TYPE = 'nmo.browser.connection.type';

/**
 * A random uuid saved in local storage. If uuid does not exist in storage, we create a new one and save it.
 */
export const NMO_SEMATTRS_NMO_BROWSER_LOCAL_STORAGE_ID = 'nmo.browser.local_storage.id';

/**
 * A random uuid saved in session storage. If uuid does not exist in storage, we create a new one and save it.
 */
export const NMO_SEMATTRS_NMO_BROWSER_SESSION_STORAGE_ID = 'nmo.browser.session_storage.id';

/**
 * Date (ISO string) for when the storage.estimate attributes were measured. https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/estimate
 *
 * Since navigator.storage.estimate() returns a promise, we don't call it on every span start. So, it might be a little bit out of date or not present at all, when the page just loaded.
 */
export const NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_MEASURED_AT = 'nmo.browser.storage.estimate.measured_at';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/estimate
 *
 * See {@link NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_MEASURED_AT} for when the estimate was taken
 */
export const NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_QUOTA = 'nmo.browser.storage.estimate.quota';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/estimate
 *
 * See {@link NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_MEASURED_AT} for when the estimate was taken
 */
export const NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_USAGE = 'nmo.browser.storage.estimate.usage';

/**
 * usage/quota https://developer.mozilla.org/en-US/docs/Web/API/StorageManager/estimate
 *
 * See {@link NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_MEASURED_AT} for when the estimate was taken
 */
export const NMO_SEMATTRS_NMO_BROWSER_STORAGE_ESTIMATE_USAGE_REL = 'nmo.browser.storage.estimate.usage_rel';

/**
 * Current state as documented in [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api)
 *
 * Note: This might not be 100% accurate all of the time (but should work well enough). And things (can) differ somewhat between browsers (not added (Google introduced) Lifecycle API stuff, differing implementations regarding events/discarding/terminating?)
 *
 * Note 2: Is set on span start (as pretty much all attributes are). See {@link NMO_SEMATTRS_NMO_BROWSER_LIFECYCLE_API_STATES} for all states
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_STATE = 'nmo.browser.page_lifecycle.state';

/**
 * All States in the spans lifetime, as documented in [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api)
 *
 * Note: This might not be 100% accurate all of the time (but should work well enough). And things (can) differ somewhat between browsers (not added (Google introduced) Lifecycle API stuff, differing implementations regarding events/discarding/terminating?)
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_STATES = 'nmo.browser.page_lifecycle.states';

/**
 * document.wasDiscarded as documented in [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api#events)
 *
 * Note: Not available in all browsers (yet, at time of writing).
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_WAS_DISCARDED = 'nmo.browser.page_lifecycle.was_discarded';

/**
 * Tracking whether the page had been frozen before. See [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api#events)
 *
 * Note: Things (can) differ somewhat between browsers (not added (Google introduced) Lifecycle API stuff, differing implementations regarding events/discarding/terminating?)
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_WAS_FROZEN = 'nmo.browser.page_lifecycle.was_frozen';

/**
 * Tracking whether the page had been persisted to a cache (as reported by a specific property; see note). Mainly this should mean that the page has been loaded from a cache, but it could also be present when the page is (about to be) added to a cache (see back/forward cache, which this should atm only concern!?).
 *
 * Note: Saved on pageshow and pagehide events ([PageTransitionEvents](https://developer.mozilla.org/en-US/docs/Web/API/PageTransitionEvent)). Also see [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api#events)
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_IS_OR_WAS_PERSISTED =
  'nmo.browser.page_lifecycle.is_or_was_persisted';

/**
 * Tracking whether the [additional "Page Lifecycle API" stuff as introduced by Google](https://developer.chrome.com/docs/web-platform/page-lifecycle-api#events) is supported.
 *
 * Note: We can still say something about lifecycle states and we do that in the other attributes under "nmo.browser.page_lifecycle.", but things differ a bit (see linked docs (and implementation, if the details become that important to your use case))
 */
export const NMO_SEMATTRS_NMO_BROWSER_PAGE_LIFECYCLE_NEW_API_IS_SUPPORTED =
  'nmo.browser.page_lifecycle.new_api_is_supported';

/**
 * If something to do with instrumentation itself has gone wrong, we want to record error information here. We can't use the normal span error attributes, because that would indicate that the operation that the span represents has an error.
 */
export const NMO_SEMATTRS_NMO_INSTRUMENTATION_INTERNAL_ERROR = 'nmo.instrumentation_internal.error';

/**
 * Result of the operation that is represented by the span. Optional, and often you don't need it. Use however necessary, although you might get the most benefit by using grouping and filtering, if this is low-cardinality (and you have extra attributes for any high-cardinality data).
 */
export const NMO_SEMATTRS_NMO_OPERATION_RESULT = 'nmo.operation.result';

/**
 * If the piano script is blocked by the browser, we want to record this information here.
 */
export const NMO_SEMATTRS_NMO_PIANO_SCRIPT_BLOCKED = 'nmo.piano.script_blocked';
